import type {
  TerminalBenchmarkRowModel,
  TerminalMetricRowModel,
} from "@/lib/terminal/company-file"
import { metricDefinitions } from "@/lib/terminal/file-label-definitions"
import { toneTextClass } from "@/lib/terminal/tone"
import { cn } from "@/lib/utils"

import { DefinitionLabel } from "./definition-tooltip"
import { StarUnit } from "./star-unit"

export const MetricRow = ({
  row,
  isFirst,
}: {
  row: TerminalMetricRowModel
  isFirst?: boolean
}) => {
  const isInsufficient = row.value === "Insufficient Data"
  const isEmpty = isInsufficient || row.value === "None detected"

  // In File data a number is never coloured; only its "vs portfolio" delta is.
  return (
    <div className={cn("file-row items-center", !isFirst && "file-row-rule")}>
      <DefinitionLabel
        definition={metricDefinitions[row.label]}
        className="type-row-label text-muted-foreground flex-1"
      >
        {row.label}
      </DefinitionLabel>

      <span
        className={cn(
          "type-data inline-flex min-w-12.5 items-center justify-end text-right",
          isEmpty ? "text-muted-foreground font-normal" : "text-foreground",
          isInsufficient && "italic"
        )}
      >
        {displayValue(row.value)}
        {!isEmpty && row.unit === "star" && (
          <StarUnit className="ml-1 inline-block" />
        )}
      </span>
    </div>
  )
}

export const BenchmarkRows = ({
  rows,
}: {
  rows: TerminalBenchmarkRowModel[] | undefined
}) => {
  const visibleRows = rows ?? []
  if (visibleRows.length === 0) return null

  return (
    <div className="pb-[var(--file-benchmark-group-padding-bottom)]">
      {visibleRows.map((benchmark) => (
        <div
          key={benchmark.label}
          className="type-micro text-muted-foreground flex justify-end gap-2 py-[var(--file-benchmark-row-padding-y)] font-normal tracking-normal normal-case"
        >
          {/* Delta-only (reference v2.11.0): the raw baseline value is dropped —
              the comparison IS the signal, not the absolute portfolio number. */}
          <span className="text-text-disabled w-32.5 text-right">
            {benchmark.label}
          </span>
          <span
            className={cn(
              "w-13.5 text-right tabular-nums",
              toneTextClass[benchmark.tone]
            )}
          >
            {displayValue(benchmark.delta)}
          </span>
        </div>
      ))}
    </div>
  )
}

const displayValue = (value: string) => {
  if (value === "--") return "—"
  return value
}
